<template>
  <app-page labelledby="page-title">
    <page-header title="Тарифный план" description="Текущий баланс, подключенные модули и доступные рабочие места.">
      <template #status><status-badge :label="balanceTone.label" :tone="balanceTone.tone" :icon="balanceTone.icon" /></template>
      <template #actions>
        <q-btn flat no-caps icon="history" label="История платежей" :to="{ name: 'org_invoices_history' }" />
        <q-btn v-if="canChangeBilling" color="primary" no-caps icon="add" label="Пополнить баланс" @click="paymentDialog = true" />
      </template>
    </page-header>

    <app-state v-if="state !== 'ready'" :state="state" :title="stateTitle" :message="stateMessage" @retry="requestData" />
    <template v-else>
      <page-section title="Состояние тарифа" description="Расчет выполняется по активным модулям и количеству дополнительных мест.">
        <dl class="tariff-summary">
          <div><dt>Баланс</dt><dd>{{ money(accountBalance) }}</dd></div>
          <div><dt>Списание в день</dt><dd>{{ money(pricePerDay) }}</dd></div>
          <div><dt>Период работы</dt><dd>{{ coverageLabel }}</dd></div>
          <div><dt>Рабочие места</dt><dd>{{ rolesCount }} из {{ workplaces }}</dd></div>
        </dl>
      </page-section>

      <page-section title="Модули" :description="canChangeBilling ? 'В режиме редактирования можно изменить состав тарифа и дополнительные места.' : 'Настройки доступны только для просмотра.'">
        <template #actions>
          <q-btn v-if="canChangeBilling && !editMode" flat no-caps icon="edit" label="Изменить тариф" @click="startEditing" />
        </template>
        <div class="module-list">
          <div v-for="module in modules" :key="module.id" class="module-row">
            <q-checkbox v-if="editMode" v-model="module.is_active" :disable="cannotToggle(module)" :aria-label="`Подключить модуль ${module.template.name}`" @update:model-value="calculateBilling" />
            <status-badge v-else :label="module.is_active ? 'Подключен' : 'Отключен'" :tone="module.is_active ? 'success' : 'neutral'" :icon="module.is_active ? 'check_circle' : 'remove_circle_outline'" />
            <div class="module-row__content">
              <strong>{{ module.template.name }}</strong>
              <span>{{ money(module.template.price) }} в день · {{ module.template.workplaces }} базовых мест</span>
            </div>
            <div v-if="editMode && module.template.is_required" class="workplace-stepper" :aria-label="`Дополнительные места: ${module.template.name}`">
              <q-btn flat round dense icon="remove" aria-label="Уменьшить количество мест" :disable="!module.is_active || module.workplaces <= minimumAdditional(module)" @click="changeAdditional(module, -1)" />
              <span>{{ module.workplaces }}</span>
              <q-btn flat round dense icon="add" aria-label="Увеличить количество мест" :disable="!module.is_active" @click="changeAdditional(module, 1)" />
            </div>
            <span v-else class="module-row__places">{{ module.template.workplaces + module.workplaces }} мест</span>
          </div>
        </div>
        <div v-if="editMode" class="tariff-actions">
          <q-btn flat no-caps label="Отмена" @click="cancelEditing" />
          <q-btn color="primary" no-caps label="Сохранить" :loading="saving" @click="saveBilling" />
        </div>
        <p v-if="!canChangeBilling" class="readonly-note">Для изменения тарифа и пополнения баланса нужны права «Тарифы и счета».</p>
      </page-section>
    </template>

    <q-dialog v-model="paymentDialog" persistent>
      <q-card class="payment-dialog">
        <q-card-section><h2>Пополнить баланс</h2><p>Будет создан реальный счет на оплату в документах организации.</p></q-card-section>
        <q-card-section><q-input :model-value="moneyInput(paymentAmount, 'paymentAmount')" @update:model-value="setMoney('paymentAmount', $event)" @blur="finishMoney('paymentAmount')" type="number" min="1" step="1" label="Сумма, ₽" autofocus :rules="[() => Number(paymentAmount) > 0 || 'Введите сумму больше нуля']" /></q-card-section>
        <q-card-actions align="right">
          <q-btn flat no-caps label="Отмена" :disable="paymentLoading" v-close-popup />
          <q-btn color="primary" no-caps label="Создать счет" :loading="paymentLoading" :disable="paymentAmount <= 0" @click="createPayment" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </app-page>
</template>

<script>
import { formatMoney } from '../../services/moneyFormat'
import AppPage from '../../components/ui/AppPage.vue'
import AppState from '../../components/ui/AppState.vue'
import PageHeader from '../../components/ui/PageHeader.vue'
import PageSection from '../../components/ui/PageSection.vue'
import StatusBadge from '../../components/ui/StatusBadge.vue'

export default {
  name: 'TariffPlan',
  components: { AppPage, AppState, PageHeader, PageSection, StatusBadge },
  data() {
    return {
      state: 'loading', stateTitle: '', stateMessage: '', billingLoaded: false, structureLoaded: false, permissionLoaded: false,
      canChangeBilling: false, modules: [], originalModules: [], rolesCount: 0, workplaces: 0,
      accountBalance: 0, pricePerDay: 0, editMode: false, saving: false,
      paymentDialog: false, paymentAmount: 500, paymentLoading: false, editingMoney: ''
    }
  },
  computed: {
    remainingDays() { return this.pricePerDay > 0 ? Math.max(0, Math.floor(this.accountBalance / this.pricePerDay)) : null },
    coverageLabel() { return this.remainingDays === null ? 'Без ежедневных списаний' : `${this.remainingDays} ${this.pluralize(this.remainingDays, ['день', 'дня', 'дней'])}` },
    balanceTone() {
      if (this.accountBalance <= 0) return { label: 'Требуется пополнение', tone: 'danger', icon: 'error_outline' }
      if (this.remainingDays !== null && this.remainingDays < 7) return { label: 'Баланс заканчивается', tone: 'warning', icon: 'schedule' }
      return { label: 'Тариф активен', tone: 'success', icon: 'check_circle' }
    }
  },
  created() {
    this.onBilling = (response) => {
      if (response.error || !response.data) { this.showError('Не удалось загрузить тариф'); return }
      this.accountBalance = Number(response.data.balance || 0)
      this.modules = this.cloneModules(response.data.modules || []); this.originalModules = this.cloneModules(this.modules)
      this.calculateBilling(); this.billingLoaded = true; this.finishLoading()
    }
    this.onStructure = (response) => { if (!response.error) this.rolesCount = Number(response.roles_count || 0); this.structureLoaded = true; this.finishLoading() }
    this.onUser = (response) => { this.canChangeBilling = response.user?.role?.permissions?.change_billing === true; this.permissionLoaded = true; this.finishLoading() }
    this.onSaved = (response) => {
      this.saving = false
      if (response.error) { this.$q.notify({ color: 'negative', message: 'Не удалось сохранить тариф' }); return }
      this.editMode = false; this.originalModules = this.cloneModules(this.modules); this.$q.notify({ color: 'positive', message: 'Тарифный план сохранен' })
    }
    this.onPayment = (response) => {
      this.paymentLoading = false
      if (response.error || !response.data?.id) { this.$q.notify({ color: 'negative', message: 'Не удалось создать счет' }); return }
      this.paymentDialog = false; this.$q.notify({ color: 'positive', message: 'Счет на оплату создан' })
      this.$router.push({ name: 'document_info_bill_to_pay', params: { id: response.data.id, history_id: 'last' } })
    }
    this.emitter.on('onBillingDetails', this.onBilling); this.emitter.on('didReceiveOrgStructure', this.onStructure)
    this.emitter.on('didReceoveUserInfoAcrossOrg', this.onUser); this.emitter.on('onSaveBillingDetails', this.onSaved); this.emitter.on('onCreatePayment', this.onPayment)
    if (this.connection.isAuthenticated) this.requestData(); else this.emitter.on('connectionReady', this.requestData)
  },
  beforeUnmount() {
    this.emitter.off('onBillingDetails', this.onBilling); this.emitter.off('didReceiveOrgStructure', this.onStructure)
    this.emitter.off('didReceoveUserInfoAcrossOrg', this.onUser); this.emitter.off('onSaveBillingDetails', this.onSaved); this.emitter.off('onCreatePayment', this.onPayment)
    this.emitter.off('connectionReady', this.requestData)
  },
  methods: {
    moneyInput(value, field) {
      if (this.editingMoney === field) return value
      return formatMoney(value, { empty: '', invalid: String(value), useGrouping: false }).replace(',', '.')
    },
    setMoney(field, value) {
      this.editingMoney = field
      const number = Number.parseFloat(value)
      this[field] = Number.isNaN(number) ? value : number
    },
    finishMoney(field) {
      if (this.editingMoney === field) this.editingMoney = ''
    },
    requestData() {
      this.state = 'loading'; this.billingLoaded = false; this.structureLoaded = false; this.permissionLoaded = false
      this.connection.getBillingDetails(this.connection.makeRequestId('tariff')); this.connection.getOrgStructure(this.connection.makeRequestId('tariff-structure')); this.connection.getUserInfoAcrossOrg(this.connection.makeRequestId('tariff-permission'))
    },
    finishLoading() { if (this.billingLoaded && this.structureLoaded && this.permissionLoaded && this.state !== 'error') this.state = 'ready' },
    showError(title) { this.state = 'error'; this.stateTitle = title; this.stateMessage = 'Проверьте подключение и повторите запрос.' },
    cloneModules(modules) { return modules.map((module) => ({ ...module, workplaces: Number(module.workplaces || 0), template: { ...module.template, workplaces: Number(module.template?.workplaces || 0), price: Number(module.template?.price || 0), price_per_workplace: Number(module.template?.price_per_workplace || 0) } })) },
    calculateBilling() {
      const active = this.modules.filter((module) => module.is_active)
      this.pricePerDay = active.reduce((total, module) => total + module.template.price + module.template.price_per_workplace * module.workplaces, 0)
      this.workplaces = active.reduce((total, module) => total + module.template.workplaces + module.workplaces, 0)
    },
    startEditing() { this.originalModules = this.cloneModules(this.modules); this.editMode = true },
    cancelEditing() { this.modules = this.cloneModules(this.originalModules); this.calculateBilling(); this.editMode = false },
    minimumAdditional(module) {
      const otherPlaces = this.modules.filter((item) => item.id !== module.id && item.is_active).reduce((total, item) => total + item.template.workplaces + item.workplaces, 0)
      return Math.max(0, this.rolesCount - otherPlaces - module.template.workplaces)
    },
    changeAdditional(module, amount) { module.workplaces = Math.max(this.minimumAdditional(module), module.workplaces + amount); this.calculateBilling() },
    cannotToggle(module) { return module.template.is_required === true || (module.is_active && this.workplaces - module.template.workplaces - module.workplaces < this.rolesCount) },
    saveBilling() { this.saving = true; this.connection.saveBillingDetails(this.connection.makeRequestId('tariff-save'), this.modules) },
    createPayment() { if (Number(this.paymentAmount) <= 0) return; this.paymentLoading = true; this.connection.createPayment(this.connection.makeRequestId('payment-create'), Number(this.paymentAmount), '', 'income') },
    money(value) { return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(Number(value || 0)) },
    pluralize(value, forms) { const abs = Math.abs(value) % 100; const last = abs % 10; if (abs > 10 && abs < 20) return forms[2]; if (last > 1 && last < 5) return forms[1]; if (last === 1) return forms[0]; return forms[2] }
  }
}
</script>

<style scoped>
.tariff-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--sid-space-4); margin: 0; }
.tariff-summary div { padding-left: var(--sid-space-3); border-left: 2px solid var(--sid-color-line-strong); }
.tariff-summary dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.tariff-summary dd { margin: var(--sid-space-1) 0 0; font-size: var(--sid-font-size-section); font-weight: var(--sid-font-weight-semibold); }
.module-list { border-top: 1px solid var(--sid-color-line); }
.module-row { display: flex; min-height: var(--sid-row-comfortable); align-items: center; gap: var(--sid-space-3); padding: var(--sid-space-3) 0; border-bottom: 1px solid var(--sid-color-line); }
.module-row__content { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.module-row__content span, .module-row__places, .readonly-note { color: var(--sid-color-text-secondary); }
.workplace-stepper { display: flex; align-items: center; gap: var(--sid-space-2); }
.workplace-stepper span { min-width: 2ch; text-align: center; font-variant-numeric: tabular-nums; }
.tariff-actions { display: flex; justify-content: flex-end; gap: var(--sid-space-2); margin-top: var(--sid-space-4); }
.payment-dialog { width: min(28rem, calc(100vw - 2 * var(--sid-space-4))); }
.payment-dialog h2, .payment-dialog p { margin: 0; }
.payment-dialog p { margin-top: var(--sid-space-2); color: var(--sid-color-text-secondary); }
@media (max-width: 767px) { .tariff-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 599px) {
  .tariff-summary { grid-template-columns: 1fr; }
  .module-row { align-items: flex-start; flex-wrap: wrap; }
  .module-row__content { flex-basis: calc(100% - 8rem); }
  .workplace-stepper, .module-row__places { margin-left: auto; }
  .tariff-actions { align-items: stretch; flex-direction: column-reverse; }
}
</style>
